import { useForm } from "react-hook-form";
import { UseMutationResult } from "@tanstack/react-query";
import { useMemo } from "react";
import { TextInput } from "./Inputs/TextInput";
import { TextInputHidden } from "./Inputs/TextInputHidden";

interface GenericFormProps<T extends Record<string, any>> {
  prototypeObject: T;
  cleanLabel?: boolean; // if true, will convert camelCase to human-readable labels
  hideInputs?: boolean; // if true, will hide all input fields
  mutation?: UseMutationResult<any, unknown, T, unknown>;
  onSuccess?: (data: T) => void;
}

export default function GenericForm<T extends Record<string, any>>({
  prototypeObject,
  mutation,
  onSuccess,
  cleanLabel = true,
  hideInputs = false,
}: GenericFormProps<T>) {
  const { handleSubmit, register, watch } = useForm<any>({
    defaultValues: prototypeObject,
  });
  
  // derive form fields from keys of prototypeObject
  const formFields = useMemo(() => {
    return Object.keys(prototypeObject).map((key, index) => ({
      id: index,
      name: key as keyof T,
      label: cleanLabel ? tokenToLabel(key) : key,
      type: typeof prototypeObject[key as keyof T],
    }));
  }, [prototypeObject]);

  const onSubmit = (data: T) => {
    if (mutation) {
      mutation.mutate(data, {
        onSuccess: (response) => {
          if (onSuccess) {
            onSuccess(response);
          }
        },
      });
    } else onSuccess?.(data);
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="grid grid-cols-1 gap-x-4 gap-y-4 sm:gap-y-4"
    >
      {formFields.map((field, idx) => {
        return hideInputs ? (
          <TextInputHidden
            key={idx}
            label={field.label}
            register={register}
            registerName={field.name as string}
          />
        ) :(
          <TextInput
            key={idx}
            label={field.label}
            register={register}
            registerName={field.name as string}
            customClasses="shadow-sm focus:ring-primary-500 
              focus:border-primary-500 block w-full sm:text-sm 
              border-gray-300 rounded-md"
          />
        );
      })}
      <div className="flex justify-end mt-4">
        <button
          type="submit"
          className="bg-primary-600 hover:bg-primary-700 rounded-md px-4 py-2 text-white"
        >
          Save
        </button>
      </div>
    </form>
  );
}

function tokenToLabel(token: string) {
  return token
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}